"use client";

import React from "react";
import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import {
  formatChartTimeTick,
  formatChartTimeTooltip,
} from "@/lib/agent/chart-axis-utils";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/shadcn-components/ui/chart";
import type { TooltipContentProps } from "recharts";
import {
  AGENT_CHART_COLORS,
  type AgentChartModel,
} from "./useAgentChartModel";

function PieChartTooltip({
  active,
  payload,
}: // recharts injects these at render time; `TooltipContentProps` exposes
// `payload`/`active` (unlike `TooltipProps`). `Partial` keeps them optional so
// this can be used as a bare `<PieChartTooltip />` element in `content=`.
Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as
    | { label?: string; rawValue?: number; value?: number }
    | undefined;
  if (!row) return null;
  const signed =
    typeof row.rawValue === "number" ? row.rawValue : Number(row.value ?? 0);
  return (
    <div className="rounded-md border border-border/50 bg-background px-2 py-1 text-[10px] shadow-md">
      <p className="font-medium text-foreground">{row.label}</p>
      <p className="text-muted-foreground">
        {signed >= 0 ? "+" : ""}
        {signed.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </p>
    </div>
  );
}

interface AgentChartCanvasProps {
  model: AgentChartModel;
  className?: string;
  heightClass?: string;
}

export function AgentChartCanvas({
  model,
  className,
  heightClass,
}: AgentChartCanvasProps) {
  const {
    spec,
    seriesKeys,
    chartData,
    chartConfig,
    isMultiLine,
    isNumericTimeX,
    yDomain,
    yDomainRight,
    useDualAxis,
    seriesYAxisId,
    datasetMissing,
    hasRenderableChart,
  } = model;

  const sparseSeriesDots = isMultiLine
    ? seriesKeys.some((key) => {
        const n = chartData.filter(
          (r) => typeof r[key] === "number" && Number.isFinite(r[key]),
        ).length;
        return n > 0 && n < 40;
      })
    : false;

  if (datasetMissing) {
    return (
      <p className="text-xs text-muted-foreground px-2 py-4">
        Dataset expired — run the query again.
      </p>
    );
  }

  if (!hasRenderableChart || !spec) {
    return (
      <p className="text-xs text-muted-foreground px-2 py-4">No chart data.</p>
    );
  }

  const cartesianTooltip = (
    <ChartTooltip
      content={
        <ChartTooltipContent
          labelFormatter={(label) => {
            if (!isNumericTimeX) return String(label);
            const ms = Number(label);
            return Number.isFinite(ms)
              ? formatChartTimeTooltip(ms)
              : String(label);
          }}
        />
      }
    />
  );

  const xAxis = (
    <XAxis
      // NB: `yAxisId` is not a valid `XAxis` prop in recharts v3 — X axes are
      // keyed by `xAxisId` (defaulting to 0). recharts ignored this prop at
      // runtime, so removing it preserves the rendered output exactly.
      dataKey="x"
      type={isNumericTimeX ? "number" : "category"}
      domain={isNumericTimeX ? ["dataMin", "dataMax"] : undefined}
      tickLine={false}
      axisLine={false}
      tick={{ fontSize: 10 }}
      interval="preserveStartEnd"
      tickFormatter={(v) =>
        isNumericTimeX ? formatChartTimeTick(Number(v)) : String(v)
      }
      minTickGap={isNumericTimeX ? 32 : undefined}
    />
  );

  const yAxisLeft = (
    <YAxis
      yAxisId="left"
      domain={yDomain}
      tickLine={false}
      axisLine={false}
      tick={{ fontSize: 10 }}
      width={44}
      tickFormatter={(v) =>
        Number(v).toLocaleString(undefined, { maximumFractionDigits: 5 })
      }
    />
  );

  const yAxisRight = useDualAxis ? (
    <YAxis
      yAxisId="right"
      orientation="right"
      domain={yDomainRight ?? yDomain}
      tickLine={false}
      axisLine={false}
      tick={{ fontSize: 10 }}
      width={44}
      tickFormatter={(v) =>
        Number(v).toLocaleString(undefined, { maximumFractionDigits: 5 })
      }
    />
  ) : null;

  return (
    <ChartContainer
      config={chartConfig}
      className={cn(
        "w-full min-w-0 aspect-auto px-2 pb-2",
        heightClass ?? model.heightClass,
        className,
      )}
    >
      {spec.chartType === "pie" ? (
        <PieChart>
          <ChartTooltip content={<PieChartTooltip />} />
          <ChartLegend content={<ChartLegendContent nameKey="label" />} />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="45%"
            innerRadius={24}
            outerRadius={48}
            paddingAngle={2}
          >
            {chartData.map((entry, i) => (
              <Cell
                key={entry.label}
                fill={AGENT_CHART_COLORS[i % AGENT_CHART_COLORS.length]}
                stroke="transparent"
              />
            ))}
          </Pie>
        </PieChart>
      ) : spec.chartType === "bar" ? (
        <BarChart
          data={chartData}
          margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
        >
          {xAxis}
          {yAxisLeft}
          {yAxisRight}
          {cartesianTooltip}
          <Bar
            yAxisId="left"
            dataKey="value"
            fill="hsl(var(--primary))"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      ) : (
        <LineChart
          data={chartData}
          margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
        >
          {xAxis}
          {yAxisLeft}
          {yAxisRight}
          {cartesianTooltip}
          {isMultiLine ? (
            <>
              <ChartLegend content={<ChartLegendContent />} />
              {seriesKeys.map((key, i) => {
                const pointCount = chartData.filter(
                  (r) =>
                    typeof r[key] === "number" && Number.isFinite(r[key]),
                ).length;
                const showDots = sparseSeriesDots && pointCount > 0 && pointCount < 40;
                const seriesColor =
                  AGENT_CHART_COLORS[i % AGENT_CHART_COLORS.length];
                const yId = seriesYAxisId.get(key) ?? "left";
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    yAxisId={yId}
                    stroke={seriesColor}
                    strokeWidth={
                      key.includes("_sma") || key.includes("_ema") ? 2.5 : 1.5
                    }
                    strokeDasharray={
                      key.includes("_sma") || key.includes("_ema") ? "6 3" : undefined
                    }
                    dot={
                      showDots
                        ? {
                            r: 3,
                            fill: seriesColor,
                            stroke: seriesColor,
                            strokeWidth: 0,
                          }
                        : false
                    }
                    connectNulls={!showDots}
                    isAnimationActive={false}
                  />
                );
              })}
            </>
          ) : (
            <Line
              type="monotone"
              dataKey="value"
              yAxisId="left"
              stroke={AGENT_CHART_COLORS[0]}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}
        </LineChart>
      )}
    </ChartContainer>
  );
}
